import { useCallback, useState } from 'react';
import axios from 'axios';
import { Config } from 'config/envConfig';
import { getUrl } from '../utils';

/**
 * Downloads a server-side file by absolute path, through Node's
 * /attachments/download relay.
 *
 * The relay takes the path as-is, which is what these callers need: a chat
 * attachment sits in a repo's `uploads/` subfolder, an agent's generated file
 * in <USERDATA_PATH>/temp, and an invoice under ORG_CONFIG_PATH - three
 * different roots, only one of which FastAPI's repo-scoped /download-file/
 * knows about.
 *
 * Shared by a question's attachments, an answer's generated files, and the
 * output files in the tasks and notification panels, so there is one copy of
 * this request rather than four.
 *
 * Returns { download, loadingPath } - `loadingPath` is the path currently in
 * flight (or null), used to show a per-file spinner.
 */
export const useAttachmentDownload = () => {
    const [loadingPath, setLoadingPath] = useState(null);

    const download = useCallback(async (filePath) => {
        if (!filePath) return;
        setLoadingPath(filePath);

        try {
            let token = '';
            try {
                token = JSON.parse(localStorage.getItem('current'))?.token || '';
            } catch (e) {
                console.error('Error parsing token from localStorage:', e);
            }

            const encodedPath = encodeURIComponent(filePath);
            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/attachments/download?path=${encodedPath}`
            );

            const response = await axios.get(dynamicUrl, {
                responseType: 'blob',
                headers: { Authorization: token ? btoa(token) : '' },
                timeout: 120000,
            });

            const filename = String(filePath).split(/[/\\]/).pop() || 'download';
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            // axios returns a blob for non-2xx too, so the server's own message
            // has to be read out of it rather than off .data.
            let message = 'Download failed';
            if (err.response && err.response.data) {
                try {
                    const text = await err.response.data.text();
                    if (text) message = text;
                } catch (e) {
                    console.error('Error reading error response text:', e);
                }
            } else if (err.message) {
                message = err.message;
            }

            alert(
                `${message} ${
                    err.response?.status ? `(${err.response.status})` : ''
                }`
            );
            console.error('Download error:', err);
        } finally {
            setLoadingPath(null);
        }
    }, []);

    return { download, loadingPath };
};
